import type { z } from 'zod';

export const API_PREFIX = '/api/v1';
export const DEV_PREFIX = '/dev/v1';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * - `none`: public endpoint.
 * - `account`: requires `Authorization: Bearer <token>` of a live session (ADR 0029).
 * - `session`: like `account`, and the session must have a character chosen.
 * - `dev`: local development tooling; only served when dev tools are enabled and the
 *   request comes from the loopback interface.
 */
export type EndpointAccess = 'none' | 'account' | 'session' | 'dev';

/**
 * Single source of truth for an HTTP endpoint. The server registers handlers from it and
 * validates input; the client builds typed calls from it and validates responses.
 */
export interface EndpointDefinition {
  readonly method: HttpMethod;
  readonly path: `/${string}`;
  readonly access: EndpointAccess;
  readonly params?: z.ZodType;
  readonly query?: z.ZodType;
  readonly body?: z.ZodType;
  readonly response: z.ZodType;
  /** HTTP status for a successful response. Defaults to 200. */
  readonly successStatus?: 200 | 201;
}

export const defineEndpoint = <const E extends EndpointDefinition>(endpoint: E): E => endpoint;

type InferOr<T, Fallback> = T extends z.ZodType ? z.infer<T> : Fallback;
type InputOf<T> = T extends z.ZodType ? z.input<T> : undefined;

export type EndpointParams<E extends EndpointDefinition> = InferOr<E['params'], undefined>;
export type EndpointQuery<E extends EndpointDefinition> = InferOr<E['query'], undefined>;
export type EndpointBody<E extends EndpointDefinition> = InferOr<E['body'], undefined>;
export type EndpointResponse<E extends EndpointDefinition> = z.infer<E['response']>;

/** What a caller must provide; omitted when the endpoint takes no input of that kind. */
export type EndpointRequest<E extends EndpointDefinition> = (E['params'] extends z.ZodType
  ? { params: InputOf<E['params']> }
  : unknown) &
  (E['query'] extends z.ZodType ? { query: InputOf<E['query']> } : unknown) &
  (E['body'] extends z.ZodType ? { body: InputOf<E['body']> } : unknown);

/** Replaces `:name` segments with URL-encoded values. */
export function buildPath(path: string, params?: Record<string, string | number>): string {
  if (!params) return path;
  return path.replace(/:([A-Za-z0-9_]+)/g, (_, name: string) => {
    const value = params[name];
    if (value === undefined) throw new Error(`Missing path parameter "${name}" for ${path}`);
    return encodeURIComponent(String(value));
  });
}
